import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { withFieldCheckbox } from './withFieldsCheckbox';
import { FieldProps } from 'formik';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import { useSwitchStyles } from './styles';
import Info from 'components/Tooltip/Info';
import { getErrorMessageKey } from 'helpers/helpers';
import CheckIcon from 'components/Icons/CheckIcon';
import CancelIcon from 'components/Icons/CancelIcon';

interface OwnProps {
  name: string;
  label?: string;
  afterChange?: () => void;
  disabled?: boolean;
  tooltipContent?: React.ReactElement;
  labelWidth?: string;
  fullWidth?: boolean;
  specificError?: boolean;
  checked?: boolean;
}

type Props = OwnProps & FieldProps;

const Switch: React.FC<Props> = ({
  field,
  meta,
  label,
  afterChange,
  disabled,
  tooltipContent,
  labelWidth,
  fullWidth,
  specificError,
  checked,
}) => {
  const [callAfterChange, setCallAfterChange] = useState(false);
  const classes = useSwitchStyles({});
  const { t } = useTranslation();
  const customChecked = !!(typeof checked !== 'undefined' ? checked : field.value);

  // If there is afterChange callback
  // and the field value changed, call it.
  useEffect(() => {
    if (callAfterChange) {
      afterChange?.();
      setCallAfterChange(false);
    }
  }, [field.value, callAfterChange, afterChange]);

  const onChange = useCallback(
    (e: React.FocusEvent<any>) => {
      // If there is afterChange callback, set the flag
      // which informs the component to call the callback after
      // it do it's own onBlur and rerender with new props
      if (afterChange) {
        setCallAfterChange(true);
      }
      field.onChange(e);
    },
    [afterChange, field],
  );

  const errorMessage = useMemo(
    () => (meta.error ? t(getErrorMessageKey(meta.error, specificError)) : ''),
    [meta.error, specificError, t],
  );

  return (
    <div
      className={`${classes.root} ${disabled ? classes.disabled : ''} ${
        fullWidth ? classes.fullWidth : ''
      }`}
    >
      <input
        id={field.name}
        type="checkbox"
        className={classes.checkbox}
        {...field}
        onChange={onChange}
        disabled={disabled}
        checked={customChecked}
      />
      <label
        htmlFor={field.name}
        className={classes.label}
        style={labelWidth ? { alignItems: 'start' } : undefined}
      >
        {label && (
          <div className={classes.realLabel} style={labelWidth ? { width: labelWidth } : undefined}>
            <Typography component="span">{label}</Typography>
          </div>
        )}
        <div className={classes.switch}>
          <span>
            <CancelIcon transparentBgr htmlColor={'#fff'} />
          </span>
          <span>
            <CheckIcon transparentBgr htmlColor={'#fff'} />
          </span>
        </div>
        {tooltipContent && <Info className={classes.tooltip} content={tooltipContent} />}
      </label>
      {errorMessage && <span className={classes.error}>{errorMessage}</span>}
    </div>
  );
};

export default withFieldCheckbox(Switch);
